// Google TV / Android TV APK distribution metadata.
//
// The web admin app does not build, sign, or push APKs. It only resolves a
// download URL plus a few presentation fields (version, SHA-256, release
// notes) so an admin or end customer can install the kiosk wrapper onto a
// Google TV / Android TV device via the on-device downloader app or, for
// support cases, ADB. APK build + signing live in the CI workflow
// (.github/workflows/google-tv-apk-release.yml).
//
// Resolution order:
//   1. Explicit Vite env override (VITE_GOOGLE_TV_APK_DOWNLOAD_URL, ...).
//      Useful for self-hosted CDNs / Cloudflare R2 deployments where the
//      asset is not a GitHub Release file.
//   2. Latest GitHub Release tagged `google-tv-v*` with an `.apk` asset.
//
// We deliberately keep tag namespaces separate (`google-tv-v*` for this
// APK, `fire-tv-v*` for the Fire TV APK, `v*` for the Raspberry Pi
// image) so a release on one surface does not accidentally surface as
// the "latest Google TV download".

import { resolveGithubRepoSlug } from './githubRelease.ts';

export type GoogleTvApkErrorCode =
  | 'network'
  | 'rate_limited'
  | 'not_found'
  | 'invalid_response'
  | 'no_apk_asset';

export class GoogleTvApkError extends Error {
  code: GoogleTvApkErrorCode;

  constructor(code: GoogleTvApkErrorCode, message?: string) {
    super(message ?? code);
    this.code = code;
  }
}

export type GoogleTvApkSource = 'env-override' | 'github-release';

export interface GoogleTvApkDownload {
  source: GoogleTvApkSource;
  /** Public download URL. May be a GitHub asset URL or a configured CDN URL. */
  downloadUrl: string;
  /** Filename suggested for the saved file. */
  fileName: string;
  /** Short, human-displayable version string (e.g. "0.1.0" or "google-tv-v0.1.0"). */
  version: string | null;
  /** Hex SHA-256 of the APK, if known. Lowercase, 64 chars. */
  sha256: string | null;
  /** Optional release-notes link (CHANGELOG anchor, GitHub Release page, ...). */
  releaseNotesUrl: string | null;
  /** Always-on sideload how-to URL, used by the dialog as a "learn more" link. */
  sideloadGuideUrl: string;
  /** Repo slug used (only set when source === 'github-release'). */
  repoSlug?: string;
}

interface ResolveGoogleTvApkOptions {
  repoSlug?: string;
  fetcher?: typeof fetch;
  env?: GoogleTvApkEnv;
}

// Loosely-typed env bag so this module can be imported in a Node test runner
// without `import.meta.env` being defined.
export interface GoogleTvApkEnv {
  VITE_GOOGLE_TV_APK_DOWNLOAD_URL?: string;
  VITE_GOOGLE_TV_APK_VERSION?: string;
  VITE_GOOGLE_TV_APK_SHA256?: string;
  VITE_GOOGLE_TV_APK_RELEASE_NOTES_URL?: string;
  VITE_GITHUB_REPO?: string;
}

const GOOGLE_TV_TAG_PREFIX = 'google-tv-v';
const SHA256_PATTERN = /^[0-9a-f]{64}$/i;

// Names that strongly hint the asset is the Google TV / Android TV APK.
// Matched lower-case after stripping non-alphanumerics so `Signage Google
// TV.apk`, `signage-google-tv.apk`, `signage-googletv.apk`,
// `signage-android-tv.apk`, `signage-androidtv.apk` all rank first.
const GOOGLE_TV_NAME_HINTS = [
  'google-tv',
  'googletv',
  'android-tv',
  'androidtv',
];

interface GithubReleaseAsset {
  name?: unknown;
  browser_download_url?: unknown;
  digest?: unknown;
}

interface GithubReleaseListEntry {
  tag_name?: unknown;
  name?: unknown;
  draft?: unknown;
  prerelease?: unknown;
  html_url?: unknown;
  body?: unknown;
  assets?: unknown;
}

function readEnv(): GoogleTvApkEnv {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return (env ?? {}) as GoogleTvApkEnv;
}

function trimOrUndefined(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const t = value.trim();
  return t.length === 0 ? undefined : t;
}

function fileNameFromUrl(url: string, fallback: string): string {
  try {
    const parsed = new URL(url);
    const last = parsed.pathname.split('/').filter(Boolean).pop();
    if (last && last.toLowerCase().endsWith('.apk')) return last;
  } catch {
    // ignore — fall through to default
  }
  return fallback;
}

export function buildGoogleTvSideloadGuideUrl(repoSlug: string): string {
  return `https://github.com/${repoSlug}/blob/main/docs/GOOGLE_TV.md`;
}

export function describeGoogleTvApkError(code: GoogleTvApkErrorCode): string {
  switch (code) {
    case 'rate_limited':
      return 'GitHub API rate limit reached. Try again in a few minutes.';
    case 'network':
      return 'Could not reach GitHub. Check your connection and retry.';
    case 'not_found':
      return 'Could not find any Google TV releases for this repository.';
    case 'no_apk_asset':
      return 'Latest Google TV release has no .apk asset attached.';
    case 'invalid_response':
    default:
      return 'GitHub returned an unexpected release response.';
  }
}

function isStringLike(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function looksLikeGoogleTvAsset(name: string): boolean {
  const lower = name.toLowerCase();
  return GOOGLE_TV_NAME_HINTS.some((hint) => lower.includes(hint));
}

function pickApkAsset(
  assets: unknown,
): { name: string; url: string; digest: string | null } | null {
  if (!Array.isArray(assets)) return null;
  const entries = assets as GithubReleaseAsset[];
  // Prefer assets whose filename hints at the Google TV / Android TV target
  // so a co-released Android build for a different surface (Fire TV,
  // tablet) never confuses the resolver.
  const ranked = entries
    .filter(
      (a) =>
        isStringLike(a.name) &&
        a.name.toLowerCase().endsWith('.apk') &&
        isStringLike(a.browser_download_url),
    )
    .sort((a, b) => {
      const aGoogleTv = looksLikeGoogleTvAsset(a.name as string) ? 0 : 1;
      const bGoogleTv = looksLikeGoogleTvAsset(b.name as string) ? 0 : 1;
      return aGoogleTv - bGoogleTv;
    });
  const first = ranked[0];
  if (!first) return null;
  const digest = typeof first.digest === 'string' ? first.digest : null;
  return {
    name: first.name as string,
    url: first.browser_download_url as string,
    digest,
  };
}

function digestToSha256(digest: string | null): string | null {
  if (!digest) return null;
  // GitHub returns digests in the form "sha256:<hex>". Anything else is opaque
  // to us; ignore so we don't display the wrong algorithm.
  const m = /^sha256:([0-9a-f]{64})$/i.exec(digest);
  return m ? m[1].toLowerCase() : null;
}

function parseGithubReleaseList(
  repoSlug: string,
  list: unknown,
  sideloadGuideUrl: string,
): GoogleTvApkDownload {
  if (!Array.isArray(list)) {
    throw new GoogleTvApkError('invalid_response');
  }
  const entries = list as GithubReleaseListEntry[];

  // Find the most recent published (non-draft) release whose tag matches the
  // Google TV namespace and which exposes a usable .apk asset.
  for (const entry of entries) {
    if (entry.draft === true) continue;
    const tag = typeof entry.tag_name === 'string' ? entry.tag_name : null;
    if (!tag) continue;
    if (!tag.startsWith(GOOGLE_TV_TAG_PREFIX)) continue;

    const asset = pickApkAsset(entry.assets);
    if (!asset) continue;

    const releaseNotesUrl = typeof entry.html_url === 'string' ? entry.html_url : null;
    return {
      source: 'github-release',
      downloadUrl: asset.url,
      fileName: asset.name,
      version: tag,
      sha256: digestToSha256(asset.digest),
      releaseNotesUrl,
      sideloadGuideUrl,
      repoSlug,
    };
  }

  // Tag namespace empty: fall back to the most recent release that simply
  // contains a google-tv / android-tv-named APK. Keeps the feature usable
  // while operators are still figuring out tagging conventions.
  for (const entry of entries) {
    if (entry.draft === true) continue;
    const asset = pickApkAsset(entry.assets);
    if (!asset) continue;
    if (!looksLikeGoogleTvAsset(asset.name)) continue;
    const releaseNotesUrl = typeof entry.html_url === 'string' ? entry.html_url : null;
    const tag = typeof entry.tag_name === 'string' ? entry.tag_name : null;
    return {
      source: 'github-release',
      downloadUrl: asset.url,
      fileName: asset.name,
      version: tag,
      sha256: digestToSha256(asset.digest),
      releaseNotesUrl,
      sideloadGuideUrl,
      repoSlug,
    };
  }

  throw new GoogleTvApkError('no_apk_asset');
}

function envOverride(env: GoogleTvApkEnv, repoSlug: string): GoogleTvApkDownload | null {
  const url = trimOrUndefined(env.VITE_GOOGLE_TV_APK_DOWNLOAD_URL);
  if (!url) return null;
  // Reject obviously unsafe schemes; only allow http(s) downloads from the
  // browser. file:// / javascript: in this field would be a misconfiguration.
  if (!/^https?:\/\//i.test(url)) return null;

  const sha256Raw = trimOrUndefined(env.VITE_GOOGLE_TV_APK_SHA256);
  const sha256 =
    sha256Raw && SHA256_PATTERN.test(sha256Raw) ? sha256Raw.toLowerCase() : null;

  return {
    source: 'env-override',
    downloadUrl: url,
    fileName: fileNameFromUrl(url, 'signage-google-tv.apk'),
    version: trimOrUndefined(env.VITE_GOOGLE_TV_APK_VERSION) ?? null,
    sha256,
    releaseNotesUrl: trimOrUndefined(env.VITE_GOOGLE_TV_APK_RELEASE_NOTES_URL) ?? null,
    sideloadGuideUrl: buildGoogleTvSideloadGuideUrl(repoSlug),
  };
}

export async function resolveGoogleTvApkDownload(
  options: ResolveGoogleTvApkOptions = {},
): Promise<GoogleTvApkDownload> {
  const env = options.env ?? readEnv();
  const repoSlug = resolveGithubRepoSlug(options.repoSlug ?? env.VITE_GITHUB_REPO);
  const sideloadGuideUrl = buildGoogleTvSideloadGuideUrl(repoSlug);

  const override = envOverride(env, repoSlug);
  if (override) return override;

  const fetcher = options.fetcher ?? fetch;
  const url = `https://api.github.com/repos/${repoSlug}/releases?per_page=20`;

  let response: Response;
  try {
    response = await fetcher(url, {
      headers: {
        Accept: 'application/vnd.github+json',
      },
    });
  } catch {
    throw new GoogleTvApkError('network');
  }

  if (!response.ok) {
    if (response.status === 404) throw new GoogleTvApkError('not_found');
    if (response.status === 429) throw new GoogleTvApkError('rate_limited');
    if (response.status === 403 && response.headers.get('x-ratelimit-remaining') === '0') {
      throw new GoogleTvApkError('rate_limited');
    }
    throw new GoogleTvApkError('invalid_response');
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw new GoogleTvApkError('invalid_response');
  }

  return parseGithubReleaseList(repoSlug, json, sideloadGuideUrl);
}
